'use client';

import { useEffect } from 'react';

/**
 * This component fixes a common crash in React/Next.js applications when Google Translate 
 * is active. Google Translate directly modifies the DOM (wrapping text nodes in <font> tags), 
 * which conflicts with React's reconciliation process, leading to "Failed to execute 
 * 'insertBefore/removeChild' on 'Node'" errors.
 */
const GoogleTranslateFix = () => {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Save original methods
    const originalInsertBefore = Node.prototype.insertBefore;
    const originalRemoveChild = Node.prototype.removeChild;

    // Patch insertBefore
    Node.prototype.insertBefore = function <T extends Node>(newNode: T, referenceNode: Node | null): T {
      if (referenceNode && referenceNode.parentNode !== this) {
        if (typeof console !== 'undefined') {
          console.warn('GoogleTranslateFix: insertBefore referenceNode is not a child of this node. Skipping mutation to avoid crash.', {
            this: this,
            newNode: newNode,
            referenceNode: referenceNode
          });
        }
        return newNode;
      }
      return originalInsertBefore.call(this, newNode, referenceNode) as T;
    };

    // Patch removeChild
    Node.prototype.removeChild = function <T extends Node>(child: T): T {
      if (child.parentNode !== this) {
        if (typeof console !== 'undefined') {
          console.warn('GoogleTranslateFix: removeChild child is not a child of this node. Skipping mutation to avoid crash.', {
            this: this,
            child: child
          });
        }
        return child;
      }
      return originalRemoveChild.call(this, child) as T;
    };

    return () => {
      // Restore original methods on unmount
      Node.prototype.insertBefore = originalInsertBefore;
      Node.prototype.removeChild = originalRemoveChild;
    };
  }, []);

  return null;
};

export default GoogleTranslateFix;
